/**
 * TTS Source Constants
 *
 * Defines TTS provider options organized by category for the card-based selector.
 * All providers cost tokens - local providers are free (100% GPU discount).
 */

import sharedConstants from './shared_constants.json';

export const TTS_SOURCES = {
  // Local (GPU) providers
  KOKORO: 'kokoro',
  EDGE_TTS: 'edge_tts',
  GOOGLE: 'google',
  PYTTSX3: 'pyttsx3',
  F5_TTS: 'f5_tts',
  CHATTERBOX: 'chatterbox',

  // Cloud providers (RunPod Public Endpoints)
  MINIMAX: 'minimax',
  CHATTERBOX_TURBO: 'chatterbox_turbo',

  // Cloud providers (direct API)
  ELEVENLABS: 'elevenlabs',
} as const;

export type TTSSource = (typeof TTS_SOURCES)[keyof typeof TTS_SOURCES];

export type TTSSourceCategory = 'local' | 'cloud';

export interface TTSSourceOption {
  value: string;
  label: string;
  description: string;
  icon: string;
  pricing: string;
  badge: string | null;
  category: TTSSourceCategory;
  isLocal: boolean;
  /** Whether this is the recommended default for the collapsed preview. */
  recommended?: boolean;
}

export const TTS_SOURCE_CATEGORY_LABELS: Record<TTSSourceCategory, string> = {
  local: 'Local (GPU)',
  cloud: 'Cloud (RunPod)',
};

export const TTS_SOURCE_CATEGORY_LABELS_SERVERLESS: Record<TTSSourceCategory, string> = {
  local: 'Asgard Cloud',
  cloud: 'Cloud',
};

/** Display name of the F5-TTS model — used by the picker card AND the install gate. */
const F5_TTS_LABEL = 'F5-TTS';

/**
 * Voice cloning: which TTS provider actually speaks a user's cloned voice, and
 * what has to be on disk for it to do that on THIS machine.
 *
 * Single source of truth for both facts. `useClonedVoicesForTTS` used to assert
 * `provider: 'f5_tts'` inline for every cloned voice and check nothing else, so
 * a clone was offered, selected and sent to a provider that was not installed —
 * the generation then died mid-run with no explanation and no way forward.
 *
 * - `provider` is the id the backend's TTS registry expects for a cloned voice
 *   (`tts_module/providers/__init__.py`, registered with
 *   `supports_voice_cloning=True`).
 * - `componentId` is the id of the installable local model in the component
 *   registry (`utils/components/registry.py`), i.e. what
 *   `GET /api/v1/components` reports on and what the install gate installs.
 *   The two ids coincide today; they are separate fields because they are
 *   separate namespaces owned by different backend modules.
 *
 * Desktop only: in serverless mode F5-TTS runs on the GPU worker, there is
 * nothing local to install, and `/api/v1/components` does not exist (RULE #19).
 */
export const VOICE_CLONING_TTS = {
  provider: TTS_SOURCES.F5_TTS,
  componentId: 'f5_tts',
  name: F5_TTS_LABEL,
} as const;

/**
 * Languages voice cloning is CURATED for, from the backend's
 * `F5_TTS_VOICE_CLONING_LANGUAGES` (`constants/llm/providers.py`) via
 * shared_constants.json.
 *
 * Deliberately NOT `tts_language_support.f5_tts`: that one is "what the model
 * can synthesize at all", while this is the reviewed cloning set — Portuguese
 * was removed after the community PT-BR checkpoint tested as unintelligible,
 * and the duplicate `cmn` code for Mandarin is absent.
 *
 * The frontend used to keep its own hand-written copy of this list, which is
 * exactly how a curation decision made in Python silently fails to reach the
 * UI. No `?? []` here on purpose: if the export ever stops being generated,
 * type-check must fail loudly rather than quietly clone nothing (RULE #5).
 */
export const VOICE_CLONING_LANGUAGES: readonly string[] = Object.keys(
  sharedConstants.voice_cloning_language_support[TTS_SOURCES.F5_TTS]
);

/**
 * TTS providers whose local execution depends on an installable component,
 * mapped to that component's id in the backend registry
 * (`utils/components/registry.py`).
 *
 * THE list. Three screens ask "is this provider running locally, so this
 * request is billed at 0?" — the TTS picker card, the video-pipeline cost
 * estimate (VideoConfigForm) and the standalone Audio tab estimate
 * (AudioConfigForm). Each used to answer it with its own hand-written
 * provider check, and they drifted exactly as you would expect: every one of
 * them knew about Kokoro, none of them knew about F5-TTS once it gained a
 * ComponentManager, so a user could install F5-TTS locally and still be
 * quoted — and charged — the cloud rate.
 *
 * Keys are TTS PROVIDER ids (what the user picks, what the card renders, what
 * `tts_provider` carries). Values are COMPONENT ids (what
 * `/api/v1/components` reports on, and what the pipeline estimator's
 * availability map must be keyed by, since `_resolve_component_id` rewrites
 * kokoro -> kokoro_tts). The two are NOT interchangeable; conflating them
 * silently mis-keys the availability map and bills local work at cloud rates.
 *
 * Membership test is strictly "does this provider's install state change the
 * PRICE", verified against the backend, not "does it have a component":
 *   is_tts_provider_free('kokoro', True)  -> True   (in GPU_TTS_PROVIDERS)
 *   is_tts_provider_free('f5_tts', True)  -> True   (in GPU_TTS_PROVIDERS)
 *   is_tts_provider_free('piper',  True)  -> False  (in neither free set)
 * So "piper" is deliberately ABSENT even though piper_tts is a real component:
 * installing it changes nothing about the price on either estimator, and the
 * pipeline path cannot even reach it (the Pydantic Literal from
 * TTS_PROVIDER_IDS has no "piper", so such a request dies at validation).
 * Listing it here would make AudioConfigForm quote "Free" for a provider the
 * backend charges for — the exact divergence this map exists to prevent.
 * Piper's real home is the synchronous local-TTS endpoint's own
 * `_LOCAL_COMPONENT_BY_PROVIDER` (shared/routes/tts_generation.py), a surface
 * with no cost card.
 *
 * A provider absent from this map has no price-changing local install and is
 * always billed as cloud (RULE #5: no fallback to "capability" — Chatterbox
 * and Higgs are local-CAPABLE but have no ComponentManager, so they must not
 * be assumed free). Add a provider here and `useLocalTtsInstallStatus` will
 * fail to compile until it is taught to poll the new component.
 *
 * ⚠️ DO NOT merge this with `TTS_PROVIDER_LOCAL_COMPONENT_IDS` in
 * `constants/tts/providers.py`. They have the same shape and a similar name
 * and are NOT duplicates — the membership differs on purpose:
 *   - That one answers "which providers cannot be SERVED AT ALL until their
 *     component is installed" (availability gating). Kokoro is absent there
 *     because it ships in pyproject.toml and imports in-process; gating it
 *     would report a working provider as unavailable.
 *   - This one answers "whose install state changes the PRICE" (billing).
 *     Kokoro belongs here because an installed kokoro_tts legitimately bills 0.
 * Unifying them would either gate Kokoro (breaking a provider that works) or
 * drop F5-TTS gating (restoring the mid-generation RuntimeError that the
 * install gate was built to remove). A provider needing both behaviours goes
 * in both maps, deliberately.
 */
export const TTS_SOURCE_COMPONENT_IDS = {
  [TTS_SOURCES.KOKORO]: 'kokoro_tts',
  [VOICE_CLONING_TTS.provider]: VOICE_CLONING_TTS.componentId,
} as const;

/** A TTS provider id that has an installable local component. */
export type LocalTtsProviderId = keyof typeof TTS_SOURCE_COMPONENT_IDS;

export const TTS_SOURCE_OPTIONS: TTSSourceOption[] = [
  // ===== LOCAL (GPU) PROVIDERS =====
  {
    value: TTS_SOURCES.KOKORO,
    label: 'Kokoro',
    description: 'High-quality neural TTS',
    icon: 'Sparkles',
    pricing: '~3 tokens/1K chars',
    badge: null,
    category: 'local',
    isLocal: true,
    recommended: true,
  },
  {
    value: TTS_SOURCES.EDGE_TTS,
    label: 'Edge TTS',
    description: 'Microsoft neural voices',
    icon: 'Globe',
    pricing: '~1 token/1K chars',
    badge: null,
    category: 'local',
    isLocal: true,
  },
  {
    value: TTS_SOURCES.GOOGLE,
    label: 'Google TTS',
    description: 'Google neural voices',
    icon: 'Globe',
    pricing: '~1 token/1K chars',
    badge: null,
    category: 'local',
    isLocal: true,
  },
  {
    value: TTS_SOURCES.CHATTERBOX,
    label: 'Chatterbox',
    description: 'Emotion-aware speech',
    icon: 'MessageCircle',
    pricing: '~3 tokens/1K chars',
    badge: null,
    category: 'local',
    isLocal: true,
  },
  {
    value: TTS_SOURCES.F5_TTS,
    label: F5_TTS_LABEL,
    description: 'Voice cloning premium',
    icon: 'Mic',
    pricing: '~3 tokens/1K chars',
    badge: null,
    category: 'local',
    isLocal: true,
  },
  {
    value: TTS_SOURCES.PYTTSX3,
    label: 'PyTTSx3',
    description: 'System voices (offline)',
    icon: 'Monitor',
    pricing: '~1 token/1K chars',
    badge: null,
    category: 'local',
    isLocal: true,
  },

  // ===== CLOUD PROVIDERS (RunPod Public Endpoints) =====
  {
    value: TTS_SOURCES.MINIMAX,
    label: 'MiniMax Speech 02 HD',
    description: 'Cloud HD speech synthesis',
    icon: 'Cloud',
    pricing: '~76 tokens/1K chars',
    badge: null,
    category: 'cloud',
    isLocal: false,
  },
  {
    value: TTS_SOURCES.CHATTERBOX_TURBO,
    label: 'Chatterbox Turbo',
    description: 'Fast cloud emotion TTS',
    icon: 'Zap',
    pricing: '~14 tokens/1K chars',
    badge: null,
    category: 'cloud',
    isLocal: false,
  },
  // ElevenLabs card hidden from UI pending paid plan upgrade.
  // See: todo_list_prompts/high_priority/activateELevenLabs/REACTIVATION_PLAN.md
];

export const DEFAULT_TTS_SOURCE: TTSSource = 'kokoro';

// Providers that are free on EVERY execution path (desktop AND Cloud Run)
// because they never consume Asgard GPU/RunPod infra -- no model to install,
// no "not installed yet" state. Single source of truth:
// ALWAYS_FREE_TTS_PROVIDERS in constants/pricing/tts.py, synced here via
// shared_constants.json (see scripts/generate_shared_constants.py). Unlike
// `isLocal` above (a CAPABILITY/category flag -- "has a local execution
// path"), this answers "is THIS request billed free" -- the exact
// distinction whose confusion caused the same provider to show "Free" on one
// screen and the cloud price on another (regression: 2026-08).
const ALWAYS_FREE_TTS_PROVIDER_IDS: readonly string[] =
  sharedConstants.constants.tts.ALWAYS_FREE_TTS_PROVIDERS ?? [];

export function isAlwaysFreeTtsSource(value: string): boolean {
  return ALWAYS_FREE_TTS_PROVIDER_IDS.includes(value);
}
